
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Hotel, Room, RoomFormData } from "@/types/hotel";
import { RoomDialogBasicInfo } from "./RoomDialog/RoomDialogBasicInfo";
import { RoomDialogPricing } from "./RoomDialog/RoomDialogPricing";
import { RoomDialogDateRange } from "./RoomDialog/RoomDialogDateRange";
import { RoomDialogWeeklyPricing } from "./RoomDialog/RoomDialogWeeklyPricing";
import { RoomImageUpload } from "./RoomImageUpload";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

interface RoomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingRoom: Room | null;
  onSubmit: (formData: RoomFormData, editingRoom: Room | null) => Promise<boolean>;
  hotels: Hotel[];
  preselectedHotelId?: string;
}

const commonAmenities = [
  'واي فاي مجاني',
  'تكييف',
  'تلفزيون',
  'ثلاجة صغيرة',
  'خزنة',
  'مجفف شعر',
  'حمام خاص',
  'بلكونة',
  'إطلالة بحرية',
  'إطلالة جبلية',
  'خدمة الغرف',
  'مكتب'
];

export const RoomDialog = ({ open, onOpenChange, editingRoom, onSubmit, hotels, preselectedHotelId }: RoomDialogProps) => {
  const [formData, setFormData] = useState<RoomFormData>({
    hotel_id: '',
    name: '',
    name_en: '',
    selling_price: 0,
    buying_price: 0,
    currency: 'SAR',
    week_prices: {},
    from_date: '',
    to_date: '',
    is_weekly_pricing: false,
    description: '',
    amenities: [],
    room_images: []
  });
  const [newAmenity, setNewAmenity] = useState('');

  useEffect(() => {
    if (editingRoom) {
      setFormData({
        hotel_id: editingRoom.hotel_id,
        name: editingRoom.name,
        name_en: editingRoom.name_en || '',
        selling_price: editingRoom.selling_price || 0,
        buying_price: editingRoom.buying_price || 0,
        currency: editingRoom.currency || 'SAR',
        week_prices: editingRoom.week_prices || {},
        from_date: editingRoom.from_date || '',
        to_date: editingRoom.to_date || '',
        is_weekly_pricing: editingRoom.is_weekly_pricing || false,
        description: editingRoom.description || '',
        amenities: editingRoom.amenities || [],
        room_images: editingRoom.room_images || []
      });
    } else if (preselectedHotelId) {
      setFormData({
        hotel_id: preselectedHotelId,
        name: '',
        name_en: '',
        selling_price: 0,
        buying_price: 0,
        currency: 'SAR',
        week_prices: {},
        from_date: '',
        to_date: '',
        is_weekly_pricing: false,
        description: '',
        amenities: [],
        room_images: []
      });
    } else {
      resetForm();
    }
  }, [editingRoom, preselectedHotelId, open]);

  const resetForm = () => {
    setFormData({
      hotel_id: preselectedHotelId || '',
      name: '',
      name_en: '',
      selling_price: 0,
      buying_price: 0,
      currency: 'SAR',
      week_prices: {},
      from_date: '',
      to_date: '',
      is_weekly_pricing: false,
      description: '',
      amenities: [],
      room_images: []
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await onSubmit(formData, editingRoom);
    if (success) {
      onOpenChange(false);
      resetForm();
    }
  };

  const handleWeekPriceChange = (day: string, price: string) => {
    setFormData(prev => ({
      ...prev,
      week_prices: {
        ...prev.week_prices,
        [day]: parseFloat(price) || 0
      }
    }));
  };

  const addAmenity = (amenity: string) => {
    if (amenity && !formData.amenities?.includes(amenity)) {
      setFormData(prev => ({
        ...prev,
        amenities: [...(prev.amenities || []), amenity]
      }));
    }
    setNewAmenity('');
  };

  const removeAmenity = (amenityToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities?.filter(amenity => amenity !== amenityToRemove) || []
    }));
  };

  const selectedHotel = hotels.find(hotel => hotel.id === formData.hotel_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>
            {editingRoom ? 'تعديل الغرفة' : 'إضافة غرفة جديدة'}
          </DialogTitle>
          <DialogDescription>
            {editingRoom ? 'قم بتعديل بيانات الغرفة' : 'قم بإدخال بيانات الغرفة الجديدة'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <RoomDialogBasicInfo 
            formData={formData}
            setFormData={setFormData}
            hotels={hotels}
            preselectedHotelId={preselectedHotelId}
            selectedHotel={selectedHotel}
          />

          {/* الوصف */}
          <div className="space-y-2">
            <Label htmlFor="description">وصف الغرفة</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="أدخل وصفاً مفصلاً للغرفة..."
              rows={3}
            />
          </div>

          {/* المرافق والخدمات */}
          <div className="space-y-3">
            <Label>المرافق والخدمات</Label>
            
            {/* المرافق الشائعة */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {commonAmenities.map(amenity => (
                <Button
                  key={amenity}
                  type="button"
                  variant={formData.amenities?.includes(amenity) ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (formData.amenities?.includes(amenity)) {
                      removeAmenity(amenity);
                    } else {
                      addAmenity(amenity);
                    }
                  }}
                  className="justify-start h-auto py-2"
                >
                  {amenity}
                </Button>
              ))}
            </div>

            {/* إضافة مرفق مخصص */}
            <div className="flex gap-2">
              <Input
                value={newAmenity}
                onChange={(e) => setNewAmenity(e.target.value)}
                placeholder="أضف مرفق مخصص..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAmenity(newAmenity);
                  }
                }}
              />
              <Button
                type="button"
                onClick={() => addAmenity(newAmenity)}
                disabled={!newAmenity.trim()}
              >
                إضافة
              </Button>
            </div>

            {/* عرض المرافق المختارة */}
            {formData.amenities && formData.amenities.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.amenities.map(amenity => (
                  <Badge key={amenity} variant="secondary" className="gap-1">
                    {amenity}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 w-4 h-4"
                      onClick={() => removeAmenity(amenity)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* صور الغرفة */}
          <RoomImageUpload
            images={formData.room_images || []}
            onImagesChange={(images) => setFormData(prev => ({ ...prev, room_images: images }))}
          />

          <RoomDialogPricing formData={formData} setFormData={setFormData} />

          <RoomDialogDateRange formData={formData} setFormData={setFormData} />

          <RoomDialogWeeklyPricing 
            formData={formData} 
            setFormData={setFormData}
            handleWeekPriceChange={handleWeekPriceChange}
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              إلغاء
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              {editingRoom ? 'تحديث' : 'إضافة'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
